import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, Box, Typography, Button } from '@mui/material';
import apiClient from '@/lib/api';
import { MainLayout, SignInHandoff } from '@/components/layout';
import { LoginPage, SSOPage } from '@/pages';
import { useAuthStore, AUTH_BYPASS_ENABLED } from '@/stores/auth.store';
import { usePermissions } from '@/hooks/use-permissions';
import { NoPermissionPage } from '@/pages/no-permission';
import { useUIStore } from '@/stores/ui.store';
import {
  armSignInHandoffPreview,
  consumeQueuedIntro,
  forgetLastAccess,
  queueIntroOnNextLoad,
  type SignInIntroMode,
} from '@/stores/sign-in-handoff.store';
import { lightTheme, darkTheme } from '@/theme';
import { colors } from '@/theme/tokens';
import { lazyWithReload, isChunkLoadError, reloadOnceForChunkError } from '@/lib/lazy-with-reload';

// Lazy-loaded pages — code-split into separate chunks. Use
// lazyWithReload so a stale chunk hash after a redeploy triggers a
// single page reload instead of dead-ending on the error boundary.
const DashboardPage = lazyWithReload(() => import('@/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const DevicesPage = lazyWithReload(() => import('@/pages/devices').then(m => ({ default: m.DevicesPage })));
const DeviceDetailPage = lazyWithReload(() => import('@/pages/device-detail').then(m => ({ default: m.DeviceDetailPage })));
const AnalyticsPage = lazyWithReload(() => import('@/pages/analytics').then(m => ({ default: m.AnalyticsPage })));
const RolesPage = lazyWithReload(() => import('@/pages/roles').then(m => ({ default: m.RolesPage })));
const PoliciesPage = lazyWithReload(() => import('@/pages/policies').then(m => ({ default: m.PoliciesPage })));
const SoftwareUpdatePage = lazyWithReload(() => import('@/pages/software-update').then(m => ({ default: m.SoftwareUpdatePage })));
const RolloutDetailPage = lazyWithReload(() => import('@/pages/rollout-detail').then(m => ({ default: m.RolloutDetailPage })));
const GroupDetailPage = lazyWithReload(() => import('@/pages/group-detail').then(m => ({ default: m.GroupDetailPage })));
const PackageDetailPage = lazyWithReload(() => import('@/pages/package-detail').then(m => ({ default: m.PackageDetailPage })));
const AuthAuditPage = lazyWithReload(() => import('@/pages/auth-audit').then(m => ({ default: m.AuthAuditPage })));
const ResourcesPage = lazyWithReload(() => import('@/pages/resources').then(m => ({ default: m.ResourcesPage })));
const AdminTenantsPage = lazyWithReload(() => import('@/pages/admin-tenants').then(m => ({ default: m.AdminTenantsPage })));
const MCPAccessPage = lazyWithReload(() => import('@/pages/mcp-access').then(m => ({ default: m.MCPAccessPage })));
const ProfilePage = lazyWithReload(() => import('@/pages/profile').then(m => ({ default: m.ProfilePage })));
const LogSharePage = lazyWithReload(() => import('@/pages/log-share').then(m => ({ default: m.LogSharePage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// MQTT filter shape — matches the server's /admin/debug/mqtt-filters API.
type MqttFilter = {
  id: string;
  device_id?: string;
  service?: string;
  action?: string;
  method?: string;
};

// Console-only helpers. Exposed in ALL builds (not just DEV) because QC
// and ops occasionally need them on deployed environments — e.g.
// `showNav('policies')` to reveal hidden sidebar items, or
// `dropUploadAck('<deviceId>')` to simulate a device failing to ACK.
// `mockScreenshot` / `__qc` stay DEV-only since they rely on React
// Query internals that shouldn't be surfaced in prod.
{
  const w = window as unknown as {
    showNav?: (key: string) => void;
    hideNav?: (key: string) => void;
    help?: () => void;
    mqttFilter?: {
      add: (f: Omit<MqttFilter, 'id'>) => Promise<MqttFilter>;
      remove: (id: string) => Promise<void>;
      clear: () => Promise<void>;
      list: () => Promise<MqttFilter[]>;
    };
    dropUploadAck?: (deviceId?: string) => Promise<MqttFilter>;
    replaySignInIntro?: (mode?: SignInIntroMode) => void;
    resetSignInIntro?: (mode?: SignInIntroMode) => void;
  };

  // Replays either sign-in intro without signing out. Needed because
  // VITE_AUTH_BYPASS never renders the login page, and because the splash is
  // rationed to a first visit or a two-day absence — so there is otherwise no
  // practical way to look at it.
  w.replaySignInIntro = (mode: SignInIntroMode = 'handoff') => {
    armSignInHandoffPreview(mode);
    console.info(`[intro] replaying the '${mode}' sign-in intro`);
  };

  // Sets up the reload test. A plain refresh deliberately shows no intro —
  // auth.store persists isAuthenticated, so a refresh is indistinguishable from
  // a restored tab and animating it would replay on every F5. This queues one
  // run explicitly, and clears the last-visit stamp so a real sign-out/sign-in
  // would splash too.
  w.resetSignInIntro = (mode: SignInIntroMode = 'splash') => {
    forgetLastAccess();
    queueIntroOnNextLoad(mode);
    console.info(
      `[intro] last-visit stamp cleared and '${mode}' queued — reload (F5) to watch it.`,
    );
  };

  // Plays a queued intro after the reload above. Guarded on being signed in so
  // it cannot fire over the login page, and one-shot, so a second F5 is quiet.
  if (useAuthStore.getState().isAuthenticated) consumeQueuedIntro();
  w.showNav = (key: string) => {
    useUIStore.getState().setHiddenNavEnabled(key, true);
    console.info(`[nav] '${key}' revealed. Run hideNav('${key}') to hide again.`);
  };
  w.hideNav = (key: string) => {
    useUIStore.getState().setHiddenNavEnabled(key, false);
    console.info(`[nav] '${key}' hidden.`);
  };

  // Admin drop-list helpers. Uses the same authenticated apiClient that
  // the app uses, so the session token is attached automatically.
  const mqttFilter = {
    add: async (f: Omit<MqttFilter, 'id'>) => {
      const resp = await apiClient.post<MqttFilter>('/admin/debug/mqtt-filters', f);
      console.info('[mqttFilter] added', resp);
      return resp;
    },
    remove: async (id: string) => {
      await apiClient.delete(`/admin/debug/mqtt-filters/${id}`);
      console.info('[mqttFilter] removed', id);
    },
    clear: async () => {
      await apiClient.delete('/admin/debug/mqtt-filters');
      console.info('[mqttFilter] cleared all');
    },
    list: async () => {
      const resp = await apiClient.get<MqttFilter[]>('/admin/debug/mqtt-filters');
      console.table(resp);
      return resp;
    },
  };
  w.mqttFilter = mqttFilter;

  // Shortcut for the common "simulate device ACK never arrives" case.
  // Matches the legacy JSON shape {"service":"Diagnostic","action":"response","of":"uploadScreen"}
  // — any uploadScreen response will be dropped server-side. Pass a
  // deviceId (machine_id) to scope the drop to one device.
  w.dropUploadAck = (deviceId?: string) =>
    mqttFilter.add({
      device_id: deviceId,
      service: 'Diagnostic',
      action: 'response',
      method: 'uploadScreen',
    });

  // `help()` prints a list of every console command this app exposes,
  // so QC/devs don't have to remember them.
  w.help = () => {
    const rows: Array<{ command: string; description: string }> = [
      { command: 'help()', description: 'Print this list' },
      { command: "showNav('policies')", description: 'Reveal a hidden sidebar item (e.g. Policies)' },
      { command: "hideNav('policies')", description: 'Hide a previously-revealed sidebar item' },
      { command: 'mqttFilter.add({ service, action, method, device_id })', description: 'Register a server-side MQTT drop-list filter' },
      { command: 'mqttFilter.list() / remove(id) / clear()', description: 'Manage active MQTT filters' },
      { command: "dropUploadAck('<deviceId?>')", description: 'Shortcut: drop uploadScreen ACKs to simulate capture failure' },
      { command: "replaySignInIntro('handoff' | 'splash')", description: 'Replay a sign-in intro right now (default: handoff)' },
      { command: "resetSignInIntro('handoff' | 'splash')", description: 'Queue an intro for the next reload, and forget the last visit (default: splash)' },
    ];
    if (import.meta.env.DEV) {
      rows.push(
        { command: '__qc', description: 'TanStack QueryClient (DEV)' },
        { command: 'mockScreenshot({ ageMinutes, brokenUrl, count })', description: 'Inject a fake screenshot row into the active list (DEV)' },
      );
    }
    console.table(rows);
  };
}

// DEV-only helpers that touch React Query internals or require the
// QueryClient instance — keep them out of prod.
if (import.meta.env.DEV) {
  const w = window as unknown as {
    __qc?: QueryClient;
    mockScreenshot?: (opts?: { ageMinutes?: number; brokenUrl?: boolean; count?: number }) => void;
  };
  w.__qc = queryClient;

  w.mockScreenshot = ({ ageMinutes = 15, brokenUrl = true, count = 1 } = {}) => {
    const lists = queryClient.getQueriesData<{ screenshots: unknown[]; total: number; limit: number; offset: number }>({
      queryKey: ['screenshots', 'list'],
    });
    if (lists.length === 0) {
      console.warn('[mockScreenshot] no active screenshot list query — open a device Screen Capture tab first.');
      return;
    }
    for (const [key, data] of lists) {
      if (!data) continue;
      const now = Date.now();
      const extras = Array.from({ length: count }).map((_, i) => {
        const id = `mock-${now}-${i}`;
        const capturedAt = new Date(now - ageMinutes * 60_000 - i * 1000).toISOString();
        return {
          id,
          device_id: 'mock-device',
          tenant_id: 'mock-tenant',
          storage_key: `mock/${id}.png`,
          file_size: 0,
          captured_at: capturedAt,
          created_at: capturedAt,
          download_url: brokenUrl
            ? `https://storage.googleapis.com/__does_not_exist__/${id}.png`
            : undefined,
        };
      });
      queryClient.setQueryData(key, {
        ...data,
        screenshots: [...extras, ...(data.screenshots ?? [])],
        total: (data.total ?? 0) + extras.length,
      });
    }
    console.info(`[mockScreenshot] injected ${count} row(s), age=${ageMinutes}min, brokenUrl=${brokenUrl}`);
  };
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const { tuplePermissions } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // User is authenticated but has no MDM permissions — show access denied
  if (user && tuplePermissions.length === 0) {
    return <Navigate to="/no-permission" replace />;
  }

  return <>{children}</>;
}

function SuperAdminGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const { isSuperAdmin } = usePermissions();
  if (!user || !isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

 
function ComingSoon(_props: { title: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        gap: 2.5,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: '#FFF3E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 36 }}>🚧</Typography>
      </Box>
      <Typography variant="h5" fontWeight={600} color="text.primary">
        This page is coming soon.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380, lineHeight: 1.5 }}>
        We're working hard to bring you this feature. Check back later!
      </Typography>
      <Button
        variant="outlined"
        href="/"
        sx={{ borderColor: '#E5E7EB', color: '#232323', '&:hover': { borderColor: colors.primary.main, color: colors.primary.main } }}
      >
        Go to Devices
      </Button>
    </Box>
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      {/* Outside the Suspense boundary on purpose. The fallback below is null,
          so while the landing chunk downloads there is nothing on screen at
          all — this is what covers that, carrying the login page's brand panel
          into the sidebar. Renders nothing unless a sign-in armed it. */}
      <SignInHandoff />
      <Suspense fallback={null}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          {/* SSO auto-login route - validates token from external apps */}
          <Route path="/sso" element={<SSOPage />} />

          {/* No permission - shown when authenticated user has no MDM role */}
          <Route path="/no-permission" element={<NoPermissionPage />} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            {/* Devices is the landing page for everyone. Dashboard lives at
                /dashboard and is super-admin-only, so "/" must not render it —
                SuperAdminGuard also bounces here, which would otherwise loop. */}
            <Route path="/" element={<Navigate to="/devices" replace />} />
            <Route path="/dashboard" element={
              <SuperAdminGuard>
                <DashboardPage />
              </SuperAdminGuard>
            } />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:id/:tab?/:subtab?" element={<DeviceDetailPage />} />
            <Route path="/analytics" element={
              <SuperAdminGuard>
                <AnalyticsPage />
              </SuperAdminGuard>
            } />
            <Route path="/users" element={<ComingSoon title="Users" />} />
            {/* TEMP: super-admin-only. Revert to: <Route path="/roles" element={<RolesPage />} /> */}
            <Route path="/roles" element={
              <SuperAdminGuard>
                <RolesPage />
              </SuperAdminGuard>
            } />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/software-update/rollouts/:rolloutId" element={<RolloutDetailPage />} />
            <Route path="/software-update/groups/:id" element={<GroupDetailPage />} />
            <Route path="/software-update/packages/:id" element={<PackageDetailPage />} />
            <Route path="/software-update/:tab?" element={<SoftwareUpdatePage />} />
            {/* TEMP: super-admin-only. Revert to: <Route path="/audit" element={<AuthAuditPage />} /> */}
            <Route path="/audit" element={
              <SuperAdminGuard>
                <AuthAuditPage />
              </SuperAdminGuard>
            } />
            <Route path="/reports" element={<ComingSoon title="Reports" />} />
            <Route path="/alerts" element={<ComingSoon title="Alerts" />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Shared log excerpt. Inside the protected tree on purpose: the
                code is unguessable but it is not what grants access — opening
                one still needs a signed-in user with log:read on that device. */}
            <Route path="/logs/s/:code" element={<LogSharePage />} />
            <Route path="/settings" element={<ComingSoon title="Settings" />} />
            <Route path="/admin/tenants" element={
              <SuperAdminGuard>
                <AdminTenantsPage />
              </SuperAdminGuard>
            } />
            <Route path="/mcp-access" element={
              <SuperAdminGuard>
                <MCPAccessPage />
              </SuperAdminGuard>
            } />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function App() {
  const { theme: themeMode } = useUIStore();
  const { initBypassMode } = useAuthStore();

  // Initialize bypass mode if enabled
  useEffect(() => {
    if (AUTH_BYPASS_ENABLED) {
      initBypassMode();
      console.log('[Auth] Bypass mode enabled - using mock admin user');
    }
  }, [initBypassMode]);

  // Vite emits "vite:preloadError" when a <link rel="modulepreload">
  // for a code-split chunk fails — different code path from the lazy()
  // dynamic-import error, so we handle it separately. Most common cause
  // is a stale chunk hash after a deploy; reload picks up the new
  // index.html and recovers.
  useEffect(() => {
    const onPreloadError = (e: Event) => {
      e.preventDefault();
      reloadOnceForChunkError();
    };
    const onUnhandled = (e: PromiseRejectionEvent) => {
      if (isChunkLoadError(e.reason)) {
        e.preventDefault();
        reloadOnceForChunkError();
      }
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => {
      window.removeEventListener('vite:preloadError', onPreloadError);
      window.removeEventListener('unhandledrejection', onUnhandled);
    };
  }, []);

  const isDark = themeMode === 'dark' ||
    (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
